import React, { useCallback, useMemo, useState } from 'react';
import { Box, Typography, Alert, useTheme, alpha } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { addDays, format } from 'date-fns';
import useHolidays from '../../../../shared/hooks/useHolidays';
import { useManualBooking } from '../../../context/ManualBookingContext';
import GradientButton from '../../../../components/buttons/GradientButton';
import { resolveLocationOpenOnHolidays } from '../../../../locations/business-hours/locationBusinessHoursUtils';

const HolidayPickersDay = React.memo(({ day, isHoliday, sx, ...other }) => {
  const isHolidayDay = isHoliday?.(day) ?? false;
  const baseSx = Array.isArray(sx) ? sx : sx ? [sx] : [];

  return (
    <PickersDay
      day={day}
      sx={[
        ...baseSx,
        isHolidayDay && {
          color: '#d32f2f',
          fontWeight: 600,
          '&.Mui-disabled': {
            color: '#d32f2f',
            opacity: 0.9,
          },
        },
      ]}
      {...other}
    />
  );
});

const DateStep = ({ onNext, onBack }) => {
  const theme = useTheme();
  const { bookingData, updateBookingData } = useManualBooking();
  const [selectedDate, setSelectedDate] = useState(bookingData?.date || new Date());
  const [viewedMonth, setViewedMonth] = useState(bookingData?.date || new Date());
  const [error, setError] = useState(null);

  const viewedYear = viewedMonth.getFullYear();
  const { holidays: holidaysThisYear } = useHolidays(viewedYear);
  const { holidays: holidaysNextYear } = useHolidays(viewedYear + 1);
  const holidays = useMemo(() => {
    const merged = [...(holidaysThisYear || []), ...(holidaysNextYear || [])];
    const seen = new Set();
    return merged.filter((holiday) => {
      if (seen.has(holiday.date)) return false;
      seen.add(holiday.date);
      return true;
    });
  }, [holidaysThisYear, holidaysNextYear]);

  const openOnHolidays = useMemo(
    () => resolveLocationOpenOnHolidays(bookingData.selectedLocation, bookingData.companyData),
    [bookingData.selectedLocation, bookingData.companyData]
  );
  const isHoliday = useCallback(
    (date) => {
      if (!holidays?.length || openOnHolidays) return false;
      return holidays.some((holiday) => holiday.date === format(date, 'yyyy-MM-dd'));
    },
    [holidays, openOnHolidays]
  );

  const holidaysInViewedMonth = useMemo(() => {
    if (!holidays?.length || openOnHolidays) return [];

    const year = viewedMonth.getFullYear();
    const month = viewedMonth.getMonth();
    return holidays.filter((holiday) => {
      const [holidayYear, holidayMonth] = holiday.date.split('-').map(Number);
      return holidayYear === year && holidayMonth === month + 1;
    });
  }, [holidays, openOnHolidays, viewedMonth]);

  const maxDaysInAdvance = bookingData?.companyData?.settings?.bookingSettings?.maxDaysInAdvance;
  const maxDate = useMemo(() => {
    if (typeof maxDaysInAdvance === 'number' && maxDaysInAdvance > 0) {
      return addDays(new Date(), maxDaysInAdvance);
    }
    return addDays(new Date(), 3650);
  }, [maxDaysInAdvance]);

  const handleContinue = () => {
    if (!selectedDate) {
      setError('Please choose a date first.');
      return;
    }

    const dayKey = format(selectedDate, 'EEEE').toLowerCase();
    const companyDay = bookingData.companyData?.settings?.companyHours?.[dayKey];

    if (!companyDay?.isOpen) {
      setError('The business is closed on that day. Please choose another date.');
      return;
    }

    if (isHoliday(selectedDate)) {
      setError('The business is closed for a holiday on that date.');
      return;
    }

    updateBookingData({
      date: selectedDate,
      time: null,
      startTime: null,
      selectedDateTime: null,
      selectedEmployee: null,
      employeeIds: [],
      employeeId: null,
      employeeName: null,
      employeeTitle: null,
      employeeIsOwner: false,
    });
    onNext();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', borderRadius: 2, p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={selectedDate}
              onChange={(newDate) => {
                if (!newDate) return;
                setSelectedDate(newDate);
                setError(null);
              }}
              onMonthChange={(month) => setViewedMonth(month)}
              minDate={new Date()}
              maxDate={maxDate}
              shouldDisableDate={(date) => isHoliday(date)}
              slots={{ day: HolidayPickersDay }}
              slotProps={{
                day: { isHoliday: openOnHolidays ? null : isHoliday },
              }}
              sx={{
                '& .MuiPickersDay-root.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {!openOnHolidays && holidaysInViewedMonth.length > 0 && (
          <Box
            sx={{
              width: '100%',
              maxWidth: 360,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.divider, 0.08),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5, textAlign: 'center' }}>
              Holidays - {format(viewedMonth, 'MMMM yyyy')}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
              {holidaysInViewedMonth.map((holiday) => (
                <li key={holiday.date}>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(`${holiday.date}T12:00:00`), 'MMM d')}: {holiday.name}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mt: 4,
          pt: 3,
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <GradientButton onClick={onBack} variant="primary">
          Back
        </GradientButton>
        <GradientButton onClick={handleContinue} variant="success">
          Continue
        </GradientButton>
      </Box>
    </Box>
  );
};

export default React.memo(DateStep);
